import type { ComponentProps, ReactNode, Ref } from "react";
import Autocomplete, { autocompleteClasses } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "@privacy/translations";
import { CircularProgress } from "@privacy/ui";
import { DynamicShared } from "../dynamicShared";
import { TenantIcon3 } from "./icons";
import { TenantOption, TenantSelect, filter, flattenTenantHierarchy } from "./TenantSelect";
import type { ConnectedTenant, TenantHierarchy } from "../api/registrations.types";
import type { InlineInputState } from "../dynamicForm/inlineForm/inputs/InlineInputState";

const AUTOCOMPLETE_INPUT = `& .${autocompleteClasses.input}`;

type TenantData = Pick<TenantHierarchy, "permissions" | "personalDataController" | "isPersonalDataController">;

type Hierarchy = Parameters<typeof flattenTenantHierarchy<TenantData>>[0];

export type TenantOptionData = ReturnType<typeof flattenTenantHierarchy<TenantData>>[number];

type TenantsSelectProps = {
  id: string;
  tenantHierarchy: Hierarchy | undefined;
  selectedTenants: ReadonlyArray<ConnectedTenant>;
  onChange: (tenants: Array<ConnectedTenant>) => void;
  renderOptionEndAdornment?: (option: TenantOptionData) => ReactNode;
  getChipSecondaryLabel?: (tenant: ConnectedTenant) => string | undefined;
  disabledTenant?: (tenant: TenantData) => boolean;
  disabledTooltip?: string;
  singleSelect?: boolean;
  keepListOpenOnSelect?: boolean;
  stackValues?: boolean;
  disabled?: boolean;
  error?: boolean;
  inputState?: InlineInputState;
  inputRootSx?: ComponentProps<typeof TextField>["sx"];
  inputRef?: Ref<HTMLInputElement>;
  onBlur?: () => void;
  onFocus?: () => void;
  onMouseOver?: () => void;
  placeholder?: string;
};

const asOption = (tenant: ConnectedTenant): TenantOptionData => ({
  id: tenant.id,
  organizationName: tenant.name,
  personalDataController: tenant.personalDataController,
  parentId: null,
  children: [],
  permissions: [],
  ancestors: [],
  level: 0,
});

export const OwnControllerLabel = ({ children }: { children: ReactNode }) => (
  <Typography
    sx={{
      ml: "auto",
      pl: 2,
      whiteSpace: "nowrap",
      fontSize: theme => theme.typography.pxToRem(12),
      color: theme => theme.palette.text.tertiary,
    }}
  >
    {children}
  </Typography>
);

export const asTenant = (option: TenantOptionData): ConnectedTenant => ({
  id: option.id,
  name: option.organizationName,
  personalDataController: option.personalDataController,
});

const SingleTenantSelect = ({
  id,
  tenantHierarchy,
  selectedTenants,
  onChange,
  disabledTenant,
  disabledTooltip,
  disabled,
  error,
  inputRootSx,
  inputRef,
  onBlur,
  onFocus,
  onMouseOver,
  placeholder,
}: TenantsSelectProps) => {
  const [selectedTenant] = selectedTenants;

  return (
    <TenantSelect
      id={id}
      blurOnSelect
      disabled={disabled}
      error={error}
      placeholder={placeholder}
      inputRef={inputRef}
      selectedId={selectedTenant?.id ?? null}
      selectedNameWhileLoading={selectedTenant?.name}
      InputProps={{ startAdornment: selectedTenant && <TenantIcon3 size={16} />, sx: inputRootSx }}
      tenantHierarchy={tenantHierarchy}
      onBlur={onBlur}
      onFocus={onFocus}
      onMouseOver={onMouseOver}
      onSelect={tenant =>
        onChange([
          { id: tenant.id, name: tenant.organizationName, personalDataController: tenant.personalDataController },
        ])
      }
      renderOption={(optionElement, tenant) => (
        <Tooltip key={tenant.id} title={disabledTenant?.(tenant) ? disabledTooltip : undefined} followCursor>
          {optionElement}
        </Tooltip>
      )}
      forceDisabledTenant={tenant => Boolean(disabledTenant?.(tenant))}
    />
  );
};

const MultiTenantsSelect = ({
  id,
  tenantHierarchy,
  selectedTenants,
  onChange,
  renderOptionEndAdornment,
  getChipSecondaryLabel,
  disabledTenant,
  disabledTooltip,
  keepListOpenOnSelect,
  stackValues,
  disabled,
  error,
  inputState,
  inputRootSx,
  inputRef,
  onBlur,
  onFocus,
  onMouseOver,
  placeholder,
}: TenantsSelectProps) => {
  const { t } = useTranslation();
  const options = tenantHierarchy ? flattenTenantHierarchy(tenantHierarchy) : [];

  const optionsById = new Map(options.map(option => [option.id, option]));

  const selected = selectedTenants.map(tenant => ({
    ...(optionsById.get(tenant.id) ?? asOption(tenant)),
    personalDataController: tenant.personalDataController,
  }));

  const lacksPermission = (option: TenantOptionData) => optionsById.has(option.id) && Boolean(disabledTenant?.(option));

  const isDisabled = (option: TenantOptionData) => Boolean(option.disabled) || lacksPermission(option);

  return (
    <Autocomplete
      fullWidth
      multiple
      openOnFocus
      id={id}
      slotProps={{ listbox: { sx: { maxHeight: 280 } } }}
      noOptionsText={t("TenantSelect.NoOptions")}
      loading={!tenantHierarchy}
      loadingText={
        <Stack sx={{ justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
          <CircularProgress size={16} />
        </Stack>
      }
      disabled={disabled}
      options={options}
      getOptionLabel={option => option.organizationName}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionDisabled={isDisabled}
      filterOptions={(unfiltered, state) => filter(unfiltered, state.inputValue)}
      onBlur={onBlur}
      onFocus={onFocus}
      onMouseOver={onMouseOver}
      renderOption={({ key, ...optionsProps }, option) => (
        <Tooltip key={key} title={lacksPermission(option) ? disabledTooltip : undefined} followCursor>
          <span>
            <TenantOption
              option={option}
              optionsProps={optionsProps}
              tenantHierarchy={tenantHierarchy}
              endAdornment={renderOptionEndAdornment?.(option)}
            />
          </span>
        </Tooltip>
      )}
      disableCloseOnSelect={keepListOpenOnSelect}
      disableClearable
      value={selected}
      onChange={(_, picked, reason, details) => {
        if (reason === "removeOption" && details && isDisabled(details.option)) return;
        onChange(picked.map(asTenant));
      }}
      renderValue={(values, getItemProps) =>
        values.map((option, index) => {
          const { onDelete, key, tabIndex } = getItemProps({ index });

          return (
            <Box key={key} sx={{ display: "flex", flexBasis: stackValues ? "100%" : "auto" }}>
              <DynamicShared.Chip
                renderIcon={iconProps => <TenantIcon3 {...iconProps} />}
                label={option.organizationName}
                secondaryLabel={getChipSecondaryLabel?.(asTenant(option))}
                tabIndex={tabIndex}
                onDelete={values.length > 1 && !isDisabled(option) ? onDelete : undefined}
                inputState={inputState}
                variant="entity"
              />
            </Box>
          );
        })
      }
      renderInput={params => (
        <TextField
          {...params}
          size="small"
          error={error}
          placeholder={selected.length === 0 ? placeholder : undefined}
          inputRef={inputRef}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              sx: { minHeight: 36, py: 0.25, alignItems: "center", gap: selected.length ? 1 : 0, ...inputRootSx },
            },
          }}
          sx={{
            [AUTOCOMPLETE_INPUT]:
              inputState === "editing" || selected.length === 0
                ? { py: "0 !important" }
                : { position: "absolute", height: 0, py: "0 !important" },
          }}
        />
      )}
    />
  );
};

export const TenantsSelect = (props: TenantsSelectProps) =>
  props.singleSelect ? <SingleTenantSelect {...props} /> : <MultiTenantsSelect {...props} />;
